import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, ArrowLeft, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('As palavras-passe não coincidem');
      return;
    }

    if (password.length < 8) {
      toast.error('A palavra-passe deve ter pelo menos 8 caracteres');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      toast.error('A palavra-passe deve conter pelo menos uma letra maiúscula');
      return;
    }
    if (!/[0-9]/.test(password)) {
      toast.error('A palavra-passe deve conter pelo menos um número');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
      toast.success('Palavra-passe alterada com sucesso!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Ocorreu um erro. Tente novamente.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (verifying) {
      return (
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#B5985A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">A verificar o link...</p>
        </div>
      );
    }

    if (!token || !tokenValid) {
      return (
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#212121] mb-4" style={{ fontFamily: 'Montserrat' }}>
            Link inválido ou expirado
          </h2>
          <p className="text-gray-500 mb-8">
            Este link de recuperação já não é válido. Por favor, peça um novo link de recuperação.
          </p>
          <Link
            to="/admin/forgot-password"
            className="inline-flex items-center gap-2 text-[#B5985A] hover:text-[#A3884A] font-medium"
          >
            Pedir novo link
          </Link>
        </div>
      );
    }

    if (success) {
      return (
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#212121] mb-4" style={{ fontFamily: 'Montserrat' }}>
            Palavra-passe alterada!
          </h2>
          <p className="text-gray-500 mb-8">
            A sua palavra-passe foi alterada com sucesso. Pode agora iniciar sessão com a nova palavra-passe.
          </p>
          <Button
            onClick={() => navigate('/admin/login')}
            className="btn-gold h-12 px-8"
          >
            Ir para o login
          </Button>
        </div>
      );
    }

    return (
      <>
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#212121]" style={{ fontFamily: 'Montserrat' }}>
            Definir nova palavra-passe
          </h2>
          <p className="text-gray-500 mt-2">
            Introduza a sua nova palavra-passe abaixo.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-4 h-4 text-[#B5985A]" />
              <span className="label-uppercase">Nova palavra-passe</span>
            </div>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres, 1 maiúscula, 1 número"
                className="pr-10 h-12 border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-4 h-4 text-[#B5985A]" />
              <span className="label-uppercase">Confirmar palavra-passe</span>
            </div>
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repita a palavra-passe"
                className="pr-10 h-12 border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full btn-gold h-12 flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5" />
                Alterar palavra-passe
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/admin/login"
            className="inline-flex items-center gap-2 text-[#B5985A] hover:text-[#A3884A] font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao login
          </Link>
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Gold accent */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#B5985A] items-center justify-center p-12">
        <div className="text-center">
          <div className="mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_reviewfunnel/artifacts/wxdmy5mp_Opini%C3%A3o%20Contacto%20Directo%20%281%29.svg"
              alt="Opinião Contacto Directo"
              className="h-16 mx-auto filter brightness-0 invert"
            />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat' }}>
            Nova Palavra-passe
          </h1>
          <p className="text-white/80 text-lg max-w-md">
            Defina uma nova palavra-passe segura para a sua conta.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_reviewfunnel/artifacts/wxdmy5mp_Opini%C3%A3o%20Contacto%20Directo%20%281%29.svg"
              alt="Opinião Contacto Directo"
              className="h-12 mx-auto"
            />
          </div>

          {renderContent()}
        </div>
      </div>
    </div>
  );
}
